import React, { useCallback, useMemo } from "react";
import styled, { css, DefaultTheme } from "styled-components";

import {
  AnyObject,
  Box,
  Button,
  ContextMenu,
  Flex,
  ItemType,
  Kit,
  pluralize,
  PopupMenu,
  Section,
  SpaceThemeNames,
  Text,
  useBoolean,
  useDrag,
  utils,
  copy,
  buildURL,
  useNavigation,
} from "@thenounproject/lingo-core";

import useMoveItems from "@actions/items/useMoveItems";
import useDuplicateItems from "@actions/items/useDuplicateItems";
import useNotifications from "@actions/useNotifications";
import { KitCallbackArgs } from "@features/content-management/modals/MoveCopyToKitModal";
import { AssetCopyTypes } from "@features/content-management/modals/MoveCopyToSectionModal";
import { textItemDragStyle } from "@features/navigation/consts";
import stripVersion from "@helpers/stripVersion";
import useBatchUpdateSections from "@redux/actions/sections/useBatchUpdateSections";
import useDeleteSection from "@redux/actions/sections/useDeleteSection";
import useDuplicateSection from "@redux/actions/sections/useDuplicateSection";
import useMoveHeading from "@redux/actions/sections/useMoveHeading";
import useMoveSection from "@redux/actions/sections/useMoveSection";
import useUpdateSection from "@redux/actions/sections/useUpdateSection";
import useShowModal, { ModalTypes } from "@redux/actions/useModals";
import { useGetNavPoint } from "@selectors/getters";

import { KitNavHeaderData } from "./KitNavHeaderItem";
import useNavPoint from "@hooks/useNavPoint";
import useCreateFileAssets from "@redux/actions/items/useCreateFileAssets";
import { getFilesFromDataTransfer } from "@helpers/getFilesFromDataTransfer";

export type KitNavSectionData = {
  kit: Kit;
  kitPath: string;
  section: Section;
  index: number;
  allSectionIds: string[];
  selected: boolean;
  canEdit: boolean;
  shareLink: string;
  headers: KitNavHeaderData[];
};

type ParentNavProps = {
  selected: boolean;
  index: number;
  hasChildren: boolean;
  dragDirection?: string;
  shouldAcceptDrag?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};

const UNTITLED = "Untitled page";

const { getColor } = utils;

const Wrapper = styled(Box)`
  position: relative;
  .overflow {
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  @media not (hover: none) {
    &:hover {
      .overflow {
        opacity: 1;
      }
    }
  }
`;

const insertBar = css<Pick<ParentNavProps, "dragDirection" | "shouldAcceptDrag" | "hasChildren">>`
  &:after {
    content: "";
    height: 2px;
    width: 100%;
    top: 0;
    left: 0;
    background: ${props => utils.getPrimaryColor(props)};
    z-index: 10;
    position: absolute;
    opacity: 0;
  }

  &:before {
    content: "";
    height: 2px;
    width: 100%;
    bottom: 0;
    left: 0;
    background: ${props => utils.getPrimaryColor(props)};
    z-index: 10;
    position: absolute;
    opacity: 0;
  }
  ${props =>
    !props.shouldAcceptDrag &&
    props.dragDirection === "top" &&
    `
  &:after {
    opacity: 1;
  }
`};
  ${props =>
    !props.shouldAcceptDrag &&
    props.dragDirection === "bottom" &&
    !props.hasChildren &&
    `
  &:before {
    opacity: 1;
  }
`};
`;

type StyleVariant = "separator" | "section" | "input";

const getThemeStyles = (variant: StyleVariant, props: AnyObject) => {
  return {
    [SpaceThemeNames.classic]: {
      separator: `
        padding: 16px 0 8px 8px;
      `,
      section: `
        font-weight: 600;
        padding: 4px 8px;
        margin-bottom: 4px;
        @media not (hover: none) {
          &:hover {
            background: ${getColor("grayLighter")}
          }
        }
        ${props.selected && `background: ${getColor("grayLighter")};`}
      `,
      // NOTE: There are some weird padding values to compensate for slight differences when rendierng the static p tag vs the input, to prevent layout shift when toggling edit mode
      input: `

        padding: ${props.isSeparator ? "14px 0 2px 8px" : "4px 8px"};
        margin-bottom: 4px;
        border-radius: 4px;
        &:hover {
          background: ${props.isSeparator ? "transparent" : getColor("grayLighter")}
        }
        input {
          font-weight: 600;
          padding: ${props.isSeparator ? "1px 0px" : "2px 0px"};
          ${props.isSeparator && `color: ${getColor("grayDarker")};`}
        }
        ${props.selected && `background: ${getColor("grayLighter")};`}

      `,
    },
    [SpaceThemeNames.scher]: {
      separator: `
        padding: 8px 4px 16px 2px;
        margin-top: 8px;
      `,
      section: `
        margin-bottom: 16px;
        padding: 2px;
        color: ${getColor("grayDarkest")};
        @media not (hover: none) {
          &:hover {
            color: ${getColor("black")};
          }
        }
        ${props.isSeparator && `color: ${getColor("grayDarker")};`}
        ${props.selected && `color: ${getColor("black")}; font-weight: 600;`}
        ${props.selected && props.hasChildren && `margin-bottom: 16px;`}
      `,
      // NOTE: There are some weird padding values to compensate for slight differences when rendierng the static p tag vs the input, to prevent layout shift when toggling edit mode
      input: `
        margin-bottom: ${props.isSeparator ? "0" : "16px"};
        padding: ${props.isSeparator ? "15px 0 14px 0px" : "3px 1px 3px"};
        color: ${getColor("grayDarkest")};
        &:hover {
          color: ${getColor("black")};
        }
        input {
          font-weight: 600;
          ${props.isSeparator && `color: ${getColor("grayDarker")};`}
        }
        ${props.selected && `color: ${getColor("black")}; font-weight: 600;`}
        ${props.selected && props.hasChildren && `margin-bottom: 16px;`}
      `,
    },
    [SpaceThemeNames.wyman]: {
      separator: `
        padding: 8px 4px 8px 8px;
        margin-top: 8px;
      `,
      section: `
        padding: 4px 8px;
        ${props.selected && `color: ${utils.getPrimaryColor(props)}; font-weight: 600;`}
      `,
      // NOTE: There are some weird padding values to compensate for slight differences when rendierng the static p tag vs the input, to prevent layout shift when toggling edit mode
      input: `
        padding: ${props.isSeparator ? "7px 0 7px 7px" : "5px 8px"};
        margin-top: ${props.isSeparator ? "8px" : "0"};
        ${props.selected && `color: ${getColor("black")}; font-weight: 600;`}
        input {
          font-weight: 600;
          ${props.isSeparator && `color: ${getColor("grayDarker")};`}
        }
      `,
    },
  }[(props.theme as DefaultTheme).themeName]?.[variant];
};

const ParentItem = styled(Flex).attrs({
  justifyContent: "space-between",
  alignItems: "center",
  transition: "all 0.3s ease",
  p: "0",
  borderRadius: "4px",
  textAlign: "left",
  position: "relative",
  cursor: "pointer",
  as: "button",
})<ParentNavProps>`
  ${utils.ellipsis};
  ${props => utils.getFont("ui.regular", null, props)};
  ${props => getThemeStyles("section", props)};

  ${insertBar};

  ${props =>
    props.shouldAcceptDrag &&
    props.dragDirection &&
    `
    background: ${getColor("grayLighter")};
  `}
  ${props => props.index === 0 && "margin-top: 0; padding-top: 0;"}
`;

const SeparatorWrapper = styled(Box)<
  Pick<ParentNavProps, "dragDirection" | "shouldAcceptDrag" | "hasChildren"> & {
    index: number;
  }
>`
  position: relative;
  ${insertBar};
  ${props => getThemeStyles("separator", props)}
  ${props => props.index === 0 && "margin-top: 0; padding-top: 0;"}
`;

const NavInput = styled(Flex)<{
  selected: boolean;
  hasChildren: boolean;
  isSeparator?: boolean;
  index: number;
}>`
  input {
    ${(props: AnyObject) =>
      utils.getFont(props.isSeparator ? "ui.smallBold" : "ui.regular", null, props)};
    background: transparent;
    width: 100%;
    line-height: 1.4;
  }
  ${(props: AnyObject) => getThemeStyles("input", props)}
  ${props => props.index === 0 && "margin-top: 0; padding-top: 0;"}
`;

const KitNavSectionItem: React.FC<KitNavSectionData> = ({
  kit,
  kitPath,
  section,
  index,
  allSectionIds,
  selected,
  shareLink,
  canEdit,
}) => {
  const navigation = useNavigation();
  const ref = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const { showNotification } = useNotifications();
  const [menuOpen, setMenuOpen, setMenuClosed] = useBoolean(false);

  const { space, portal } = useNavPoint();
  const navPoint = useGetNavPoint();
  const [isDraggingOver, setIsDraggingOver] = React.useState(false);
  const [dragType, setDragType] = React.useState<"section" | "gallery" | "heading" | "file" | null>(
    null
  );

  const shouldAcceptDrag = ["gallery", "heading", "file"].includes(dragType);
  const { showModal } = useShowModal();
  const [moveItems] = useMoveItems();
  const [duplicateItems] = useDuplicateItems();
  const [moveHeading] = useMoveHeading();
  const [updateSection] = useUpdateSection();
  const [deleteSection] = useDeleteSection();
  const [batchUpdateSections] = useBatchUpdateSections();
  const [duplicateSection] = useDuplicateSection();
  const [moveSection] = useMoveSection();
  const [createFileAssets] = useCreateFileAssets();

  const isSeparator = section.isSeparator;
  const separatorLabel = isSeparator ? section.name.replace(/^---+/, "") : null;
  const hasSeparatorLabel = isSeparator && separatorLabel?.length > 0;

  const [editing, setEditing, setNotEditing] = useBoolean(false);
  const [editValue, setEditValue] = React.useState(separatorLabel || section.name);
  const hasChildren = Boolean(section.headers?.length);

  const { id, label } = useMemo(() => {
    return {
      id: section?.id as string,
      label: (section?.name || UNTITLED) as string,
    };
  }, [section]);

  React.useEffect(() => {
    setEditValue(isSeparator ? separatorLabel : section.name);
    if (editing) {
      inputRef.current.select();
    }
  }, [editing, isSeparator, section.name, separatorLabel]);

  /**
   * Section action handlers
   */
  const onClickSectionLink = useCallback(() => {
    const queryParams = {
      kit_token: kit.shareToken || undefined,
      v: section.version,
    };
    navigation.push(buildURL(`/s/${section.urlId}/`, { space, portal }, queryParams));
  }, [kit.shareToken, navigation, portal, section.urlId, section.version, space]);

  const onDoubleClickSectionLink = useCallback(() => {
    if (!canEdit) return;
    setEditing();
  }, [canEdit, setEditing]);

  const onEditSection = useCallback(() => {
    let valueToSave = editValue.trim();
    if (isSeparator) {
      valueToSave = `---${valueToSave}`;
    }
    if (valueToSave === section.name || !valueToSave) return setNotEditing();
    void updateSection({ sectionId: id, updates: { name: valueToSave } });
  }, [editValue, id, isSeparator, section.name, setNotEditing, updateSection]);

  const onDeleteSection = useCallback(() => {
    showModal(ModalTypes.CONFIRMATION, {
      title: "Are you sure you want to delete this page?",
      message: "Deleting this page will also remove all the assets in it.",
      buttonText: "Delete",
      buttonProcessingText: "Deleting...",
      onConfirm: () =>
        deleteSection({ sectionId: id }).then(result => {
          if (!result.error && selected) {
            navigation.push(kitPath);
          }
          return result;
        }),
    });
  }, [deleteSection, id, kitPath, navigation, selected, showModal]);

  const moveSectionCallback = useCallback(
    async ({ selectedKit }: KitCallbackArgs) => {
      const { error } = await moveSection({
        kitId: selectedKit.kitId,
        fromKitId: kit.kitId,
        sectionId: id,
      });
      if (error) {
        showNotification({ message: `Unable to move section ${error.message}`, level: "error" });
      } else {
        showNotification({
          message: `Section moved to`,
          link: {
            text: selectedKit?.name || "Untitled Kit",
            url: buildURL(`/k/${selectedKit?.urlId}`, { space }, { v: 0 }),
          },
        });

        /**
         * If we move the section we're viewing, route to the top level kit.
         */
        if (navPoint.sectionId === id) {
          navigation.push(buildURL(`/k/${kit.urlId}`, { space, portal }));
        }
      }
    },
    [
      id,
      kit.kitId,
      kit.urlId,
      moveSection,
      navPoint.sectionId,
      navigation,
      portal,
      showNotification,
      space,
    ]
  );

  const copySectionCallback = useCallback(
    async ({ selectedKit, copyAs }: KitCallbackArgs) => {
      const { error } = await duplicateSection({
        kitId: selectedKit.kitId,
        sectionId: id,
        createReferences: copyAs === AssetCopyTypes.reference,
      });

      if (error) {
        showNotification({
          message: `Unable to duplicate section ${error.message}`,
          level: "error",
        });
      } else {
        showNotification({
          message: `Section copied to`,
          link: {
            text: selectedKit?.name || "Untitled Kit",
            url: buildURL(
              `/k/${selectedKit?.urlId}`,
              { space, portal: selectedKit.kitId === kit.kitId ? portal : null },
              { v: 0 }
            ),
          },
        });
      }
    },
    [duplicateSection, id, kit.kitId, portal, showNotification, space]
  );

  const onCopySection = useCallback(() => {
    showModal(ModalTypes.MOVE_COPY_TO_KIT, {
      type: "copy",
      callback: copySectionCallback,
      entityText: section.name,
    });
  }, [copySectionCallback, section.name, showModal]);

  const onMoveSection = useCallback(() => {
    showModal(ModalTypes.MOVE_COPY_TO_KIT, {
      type: "move",
      callback: moveSectionCallback,
      entityText: section.name,
    });
  }, [moveSectionCallback, section.name, showModal]);

  const onReorderSection = useCallback(
    async ({
      dragId,
      dropId,
      dragDirection,
    }: {
      dragId: string;
      dropId: string;
      dragDirection: string;
    }) => {
      /** Remove the dragged item from array */
      const sectionIds = allSectionIds.filter(id => id !== dragId);

      /** Determine the index where the section should be placed */
      const dropIndex = sectionIds.indexOf(dropId);
      const spliceIndex = dropIndex + (dragDirection === "top" ? 0 : 1);
      sectionIds.splice(spliceIndex, 0, dragId);

      const { error } = await batchUpdateSections({
        data: sectionIds.map((id, index) => {
          return { uuid: id, display_order: index * 10 };
        }),
      });
      if (error) {
        showNotification({ message: error.message, level: "error" });
      } else {
        // TODO: Migrate to query mutation
        /** Fetch the updated kit sections order */
        // void fetchKitOutline({ spaceId: space.id, kitId: kit.kitId });
      }
    },
    [allSectionIds, batchUpdateSections, showNotification]
  );

  /**
   * Drag & drop handlers
   */
  const doOnDragStart = (e: React.DragEvent) => {
    e.dataTransfer.effectAllowed = "all";
    const elem: HTMLElement = document.createElement("div");
    elem.setAttribute("style", textItemDragStyle);
    elem.innerHTML = separatorLabel || section.name || UNTITLED;
    document.body.appendChild(elem);
    e.dataTransfer.setDragImage(elem, 0, 0);
    e.dataTransfer.setData("sectionId", id);
    setTimeout(() => {
      document.body.removeChild(elem);
    }, 0);
  };

  const doOnDrop = async (e: React.DragEvent) => {
    if (e) e.preventDefault();

    const reorderItemIds = e.dataTransfer.getData("reorderItemIds");
    const fromSectionId = e.dataTransfer.getData("fromSectionId");
    const sectionId = e.dataTransfer.getData("sectionId");
    const navHeadingId = e.dataTransfer.getData("navHeadingId");
    const duplicateMode = e.altKey;

    // Separators only accept section reordering
    if (isSeparator && !sectionId) return;

    // File drop — append all files to this section
    if (e.dataTransfer.types.includes("Files")) {
      const files = await getFilesFromDataTransfer(e.dataTransfer);
      if (files.length > 0) {
        await createFileAssets({
          files,
          itemType: ItemType.asset,
          insertPosition: { kitId: kit.kitId, sectionId: id, displayOrder: "append" },
        });
      }
      return;
    }

    // Gallery item move or duplicate
    if (reorderItemIds && fromSectionId) {
      const itemArray = reorderItemIds.split(",");
      const itemIds = itemArray.map((uuid: string) => stripVersion(uuid));

      const res = duplicateMode
        ? await duplicateItems({ itemIds, toSectionId: id, createReferences: false })
        : await moveItems({ itemIds, fromSectionId, toSectionId: id });

      if (res.isSuccess) {
        showNotification({
          message: `${itemArray.length} ${pluralize("item", itemArray.length)} ${duplicateMode ? "duplicated" : "moved"} to`,
          link: {
            text: label,
            url: buildURL(`/s/${section?.urlId}`, { space, portal }),
          },
        });
      }
      return;
    }

    // Section reorder
    if (sectionId && sectionId !== id) {
      const dragDirection = d.dragDirection.vertical;
      if (dragDirection === "bottom" && hasChildren) return;
      void onReorderSection({
        dragId: sectionId,
        dropId: id,
        dragDirection: d.dragDirection.vertical,
      });
      return;
    }

    // Heading move
    if (navHeadingId && fromSectionId) {
      const res = await moveHeading({
        headingId: navHeadingId,
        fromSectionId,
        toSectionId: id,
      });
      if (res.isSuccess) {
        showNotification({
          message: "Heading moved to",
          link: {
            text: section?.name || "Untitled Section",
            url: buildURL(`/s/${section?.urlId}`, { space, portal }),
          },
        });
      }
    }
  };

  const onDragOver = (e: React.DragEvent) => {
    if (!isDraggingOver) {
      setIsDraggingOver(true);

      // Determine what's being dragged by checking dataTransfer types
      const types = Array.from(e.dataTransfer.types).map(t => t.toLowerCase());

      if (types.includes("reorderitemids")) {
        setDragType("gallery");
      } else if (types.includes("navheadingid")) {
        setDragType("heading");
      } else if (types.includes("sectionid")) {
        setDragType("section");
      } else if (types.includes("files")) {
        setDragType("file");
      }
    }
  };

  const onDragLeave = () => {
    setIsDraggingOver(false);
    setDragType(null);
  };

  const onDragEnd = () => {
    setIsDraggingOver(false);
    setDragType(null);
  };

  const d = useDrag({
    dragRef: ref,
    doOnDragStart,
    doOnDragOver: onDragOver,
    doOnDragLeave: onDragLeave,
    doOnDragEnd: onDragEnd,
    doOnDrop,
  });
  const dragProps = canEdit ? d.dragProps : {};

  const contextMenuItems = useMemo(() => {
    if (editing) return [];
    const menuItems = [];
    if (isSeparator) {
      if (canEdit) {
        menuItems.push(
          <PopupMenu.Section key="update-items">
            <PopupMenu.Item key="edit-item" onClick={setEditing} title="Edit" />
          </PopupMenu.Section>
        );
        menuItems.push(
          <PopupMenu.Section key="edit-items">
            <PopupMenu.Item key="delete-item" onClick={onDeleteSection} title="Delete" />
          </PopupMenu.Section>
        );
      }
    } else {
      if (shareLink) {
        menuItems.push(
          <PopupMenu.Section key="share-link">
            <PopupMenu.Item onClick={() => copy(shareLink)} title="Copy share link" />
          </PopupMenu.Section>
        );
      }
      if (canEdit) {
        menuItems.push(
          <PopupMenu.Section key="update-items">
            <PopupMenu.Item key="edit-item" onClick={setEditing} title="Rename Page" />
            <PopupMenu.Item onClick={onMoveSection} title="Move to..." />
            <PopupMenu.Item onClick={onCopySection} title="Copy to..." />
          </PopupMenu.Section>
        );

        menuItems.push(
          <PopupMenu.Section key="edit-items">
            <PopupMenu.Item key="delete-item" onClick={onDeleteSection} title="Delete Page" />
          </PopupMenu.Section>
        );
      }
    }
    return menuItems;
  }, [
    canEdit,
    editing,
    isSeparator,
    onCopySection,
    onDeleteSection,
    onMoveSection,
    setEditing,
    shareLink,
  ]);

  function renderContextMenus() {
    if (contextMenuItems.length === 0) return null;
    if (editing) return null;
    return (
      <>
        {/* Context menu for right click */}
        <ContextMenu data-testid="nav-context-menu" id={id} parentRef={ref}>
          {contextMenuItems}
        </ContextMenu>
        {/* Context menu for overflow button */}
        {menuOpen && (
          <PopupMenu source={`${id}-overflow-button`} close={setMenuClosed}>
            {contextMenuItems}
          </PopupMenu>
        )}
      </>
    );
  }

  function renderNavInput() {
    return (
      <NavInput
        index={index}
        width={"100%"}
        as="form"
        onSubmit={e => {
          e.preventDefault();
          onEditSection();
        }}
        isSeparator={section.isSeparator}
        selected={!section.isSeparator && selected}
        hasChildren={!section.isSeparator && hasChildren}>
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onBlur={e => {
            e.preventDefault();
            onEditSection();
          }}
          onChange={e => setEditValue(e.target.value)}
        />
      </NavInput>
    );
  }

  function renderSeparator() {
    return (
      <SeparatorWrapper
        hasChildren={false}
        ref={ref}
        index={index}
        onDoubleClick={onDoubleClickSectionLink}
        shouldAcceptDrag={shouldAcceptDrag}
        dragDirection={isDraggingOver ? d?.dragDirection?.vertical : undefined}
        {...dragProps}>
        {hasSeparatorLabel ? (
          <Text font="ui.smallBold" color="grayDarker">
            {separatorLabel}
          </Text>
        ) : (
          <Box height="1px" background="gray" my="7px" />
        )}
      </SeparatorWrapper>
    );
  }

  function renderButton() {
    if (contextMenuItems.length === 0) return;
    if (editing) return null;

    return (
      <Button
        className="overflow"
        icon="navigation.overflow"
        size="small"
        p="none"
        position="absolute"
        right={0}
        top={isSeparator ? "50%" : "2px"}
        transform={isSeparator ? "translateY(-50%)" : null}
        themeOverrides={{
          primaryColor: "transparent",
          primaryColorDark: "transparent",
          primaryColorTint: "black",
        }}
        data-popup-source={`${id}-overflow-button`}
        onClick={e => {
          e.stopPropagation();
          setMenuOpen();
        }}
      />
    );
  }

  function renderSection() {
    return (
      <ParentItem
        ref={ref}
        width={"100%"}
        index={index}
        hasChildren={hasChildren}
        selected={selected}
        shouldAcceptDrag={shouldAcceptDrag}
        dragDirection={isDraggingOver ? d?.dragDirection?.vertical : undefined}
        onClick={onClickSectionLink}
        onDoubleClick={onDoubleClickSectionLink}
        {...dragProps}>
        <Box
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            paddingRight: "12px",
          }}>
          <span style={{ color: getColor("black") }}>{section.name || UNTITLED}</span>
        </Box>
      </ParentItem>
    );
  }

  return (
    <Wrapper>
      {renderContextMenus()}
      {editing && renderNavInput()}
      {isSeparator && !editing && renderSeparator()}
      {!isSeparator && !editing && renderSection()}
      {renderButton()}
    </Wrapper>
  );
};
export default React.memo(KitNavSectionItem) as typeof KitNavSectionItem;
